import {
  Box,
  Button,
  Modal,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  TableContainer,
  TableRow,
  Table,
  TableCell,
  TableHead,
  TableBody,
  Checkbox,
  InputAdornment,
} from "@mui/material";

import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

import { tokens } from "../../theme";
import Header from "../../components/Header";
import RibbonNavbar from "../../components/RibbonNavbar";
import SearchInput from "../../components/SearchInput";

import { useTheme } from "@mui/material";
import { useState, useEffect, useCallback } from "react";
// Ensure iepaProd is correctly imported if not already defined globally
import { iepaProd } from "../../constants/global";
import { useNavigate } from "react-router-dom";
import React from "react";
import SearchableDataGrid from "../../components/SearchableDataGrid";

const UserList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState({});
  const [selectedRoleMap, setSelectedRoleMap] = useState({}); // State for selected roles in modal
  const [selectedUser, setSelectedUser] = useState(null);

  const [users, setUsers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [roles, setRoles] = useState([]); // State for all available roles
  const [error, setError] = useState(null);

  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [openAssignRoleModal, setOpenAssignRoleModal] = useState(false); // New state for Role modal
  const [filterText, setFilterText] = useState("");
  const [routeSearchTerm, setRouteSearchTerm] = useState(""); // Renamed for clarity
  const [roleSearchTerm, setRoleSearchTerm] = useState(""); // New state for role search

  // ---------------- Columns ----------------
  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 100 },
    { field: "username", headerName: "UserName", flex: 1, minWidth: 150 },
    {
      field: "displayName",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
      valueGetter: (params, row) => row?.contact?.displayName,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 150,
      valueGetter: (params, row) => row?.contact?.email,
    },
    {
      field: "phone",
      headerName: "Phone Number",
      flex: 1,
      minWidth: 150,
      valueGetter: (params, row) => row?.contact?.phone,
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
      minWidth: 200,
      valueGetter: (params, row) => row?.address?.address_1,
    },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      minWidth: 200,
      valueGetter: (params, row) => row?.userRoles?.map((obj) => obj?.name),
    },
    {
      field: "roleId",
      headerName: "Role Id",
      hideable: true,
      valueGetter: (params, row) => row?.userRoles?.map((obj) => obj?.id),
    },
  ];

  // ---------------- Fetch Users ----------------
  const fetchUsers = useCallback(() => {
    fetch(`${iepaProd.iepaUserSrvAPI}/user/all-users/org/1`)
      .then((res) => res.json())
      .then((data) => setUsers(data))
      .catch(() => setError("Error fetching user data"));
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ---------------- Fetch Routes ----------------
  const fetchRoutes = useCallback(() => {
    fetch(`${iepaProd.iepaInventorySrvAPI}/inventory/routes/active/org/1`)
      .then((res) => res.json())
      .then((data) => Array.isArray(data) && setRoutes(data))
      .catch(() => setError("Error fetching route data"));
  }, []);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  // ---------------- Fetch Roles ----------------
  const fetchRoles = useCallback(() => {
    // Assuming a standard endpoint for fetching all roles in an organization
    fetch(`${iepaProd.iepaUserSrvAPI}/user/roles/all/org/1`)
      .then((res) => res.json())
      .then((data) => Array.isArray(data) && setRoles(data))
      .catch(() => setError("Error fetching role data"));
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // ---------------- Navigate to Edit User (onEdit) ----------------
  const handleEditUser = () => {
    if (selectedRows.length > 0) {
      navigate("/ViewUser", {
        state: {
          selectedRows,
          users,
          mode: "edit",
        },
      });
    }
  };

  // ---------------- Open Assign Route Modal (onAssignRoute) ----------------
  const handleAssign = () => {
    if (selectedRows.length === 1) {
      const user = users.find((u) => u.id === selectedRows[0]);
      setSelectedUser(user);
      setOpenAssignModal(true);
    }
  };

  // ---------------- Open Assign Role Modal (onAssignRole) ----------------
  const handleAssignRole = () => {
    if (selectedRows.length === 1) {
      const user = users.find((u) => u.id === selectedRows[0]);
      setSelectedUser(user);
      setOpenAssignRoleModal(true);
    }
  };

  // ---------------- Preselect Routes on Modal Open ----------------
  useEffect(() => {
    if (openAssignModal && selectedUser) {
      const map = {};

      const routeIds =
        typeof selectedUser.routesIds === "string"
          ? selectedUser.routesIds.split(",")
          : [];

      routeIds.forEach((id) => (map[id] = true));

      setSelectedRoute(map);
    }
  }, [openAssignModal, selectedUser]);

  // ---------------- Preselect Roles on Modal Open ----------------
  useEffect(() => {
    if (openAssignRoleModal && selectedUser) {
      const map = {};

      // Assuming userRoles is an array of objects {id, name, ...}
      const currentRoles = selectedUser.userRoles || [];

      currentRoles.forEach((role) => (map[role.id] = true));

      setSelectedRoleMap(map);
    }
  }, [openAssignRoleModal, selectedUser]);

  // ---------------- Save Routes ----------------
  const handleSaveRoutes = async () => {
    const selectedRouteIds = Object.keys(selectedRoute).filter(
      (id) => selectedRoute[id],
    );

    const updatedRoute = {
      id: selectedUser.id,
      routesIds: selectedRouteIds.join(","),
      orgId: "1",
      createdBy: 1,
    };

    try {
      const response = await fetch(
        `${iepaProd.iepaUserSrvAPI}/user/assign/route/org/1`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedRoute),
        },
      );

      if (response.ok) console.log("Route saved successfully!");
      else console.error("Failed to save route.");
    } catch (e) {
      console.error("Network error occurred.", e);
    }

    fetchUsers();
    setOpenAssignModal(false);
  };

  // ---------------- Save Roles ----------------
  const handleSaveRoles = async () => {
    const selectedRoleIds = Object.keys(selectedRoleMap).filter(
      (id) => selectedRoleMap[id],
    );

    const updatedRoleData = {
      id: selectedUser.id,
      rolesIds: selectedRoleIds.join(","),
      orgId: "1",
      createdBy: 1,
    };

    try {
      const response = await fetch(
        `${iepaProd.iepaUserSrvAPI}/user/assign/role/org/1`, // Assuming this is the correct endpoint
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedRoleData),
        },
      );

      if (response.ok);
      else console.error("Failed to save role assignment.");
    } catch (e) {
      console.error("Network error occurred.", e);
    }

    fetchUsers();
    setOpenAssignRoleModal(false);
  };

  // ---------------- Filtering ----------------
  const filteredRows = users.filter((u) => {
    const q = filterText.toLowerCase();
    return (
      u.id?.toString().includes(q) ||
      u.username?.toLowerCase().includes(q) ||
      u.contact?.displayName?.toLowerCase().includes(q) ||
      u.contact?.email?.toLowerCase().includes(q) ||
      u.contact?.phone?.toLowerCase().includes(q) ||
      u.address?.address_1?.toLowerCase().includes(q)
    );
  });

  const filteredRoutes = routes.filter((r) =>
    [r.name, r.shortName, r.code].some((x) =>
      x?.toLowerCase().includes(routeSearchTerm.toLowerCase()),
    ),
  );

  const filteredRoles = roles.filter((r) =>
    [r.name, r.description].some((x) =>
      x?.toLowerCase().includes(roleSearchTerm.toLowerCase()),
    ),
  );

  useEffect(() => {}, [selectedRows]);

  const handleDeleteUsers = async () => {
    if (selectedRows.length === 0) return;

    const confirmDelete = window.confirm(
      "Are you sure you want to delete selected user(s)?",
    );

    if (!confirmDelete) return;

    try {
      const response = await fetch(`${iepaProd.iepaUserSrvAPI}/user/delete`, {
        method: "POST", // change to DELETE if backend expects DELETE
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ids: selectedRows, // sending selected user IDs
          orgId: "1",
          updatedBy: 1,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to delete users");
      }

      // 🔄 Refresh user list
      fetchUsers();

      // 🧹 Clear selection
      setSelectedRows([]);
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  return (
    <Box m="20px">
      {/* Header container with RibbonNavbar and Header title/subtitle */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="Manage Users" marginBottom="0" />

        <RibbonNavbar
          selectedRows={selectedRows}
          showAdd
          showEdit
          showView
          showAssignRoute
          showAssignRole
          onAdd={() => navigate("../AddUser")}
          onEdit={handleEditUser}
          onView={() =>
            navigate("/ViewUser", {
              state: {
                selectedRows,
                users,
                mode: "view", // 👈 pass mode
              },
            })
          }
          onDelete={handleDeleteUsers}
          onAssignRoute={handleAssign}
          onAssignRole={handleAssignRole} // Wired up to new role handler
          disabledAssignRoute={selectedRows.length === 0}
          disabledAssignRole={selectedRows.length === 0}
          disabledEdit={selectedRows.length === 0}
          disabledView={selectedRows.length === 0}
          disabledDelete={selectedRows.length === 0}
        />
      </Box>

      {/* ---------------- DataGrid ---------------- */}
      <Box
        m="0"
        height="78vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid grey",
            whiteSpace: "normal",
            wordWrap: "break-word",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
            fontSize: "14px",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: colors.blueAccent[900],
            height: "5vh",
          },
        }}
      >
        <SearchableDataGrid
          rows={filteredRows}
          columns={columns}
          checkboxSelection
          columnVisibilityModel={{ roleId: false }}
          onRowSelectionModelChange={(newSelection) => {
            setSelectedRows(newSelection);
          }}
          components={{ Toolbar: GridToolbar }}
        />
      </Box>

      {/* ---------------- Modal (Assign Route) ---------------- */}
      <Modal open={openAssignModal} onClose={() => setOpenAssignModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            p: 4,
            boxShadow: 24,
            borderRadius: 2,
          }}
        >
          {/* Close button */}
          <IconButton
            sx={{ position: "absolute", top: 8, right: 8 }}
            onClick={() => setOpenAssignModal(false)}
          >
            <CloseIcon />
          </IconButton>

          <h2>Assign Route</h2>

          <TextField
            label="Search Routes"
            variant="outlined"
            fullWidth
            margin="normal"
            onChange={(e) => setRouteSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Scrollable Table */}
          <TableContainer sx={{ maxHeight: 250, overflow: "auto" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Select</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Short</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRoutes.map((route) => (
                  <TableRow key={route.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedRoute[route.id] || false}
                        onChange={() =>
                          setSelectedRoute((prev) => ({
                            ...prev,
                            [route.id]: !prev[route.id],
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>{route.code}</TableCell>
                    <TableCell>{route.name}</TableCell>
                    <TableCell>{route.shortName}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Save button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.primary[400],
                fontWeight: "bold",
              }}
              onClick={handleSaveRoutes}
            >
              Save Route Assignment
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ---------------- Modal (Assign Role) ---------------- */}
      <Modal
        open={openAssignRoleModal}
        onClose={() => setOpenAssignRoleModal(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            p: 4,
            boxShadow: 24,
            borderRadius: 2,
          }}
        >
          {/* Close button */}
          <IconButton
            sx={{ position: "absolute", top: 8, right: 8 }}
            onClick={() => setOpenAssignRoleModal(false)}
          >
            <CloseIcon />
          </IconButton>

          <h2>Assign Role</h2>

          <TextField
            label="Search Roles"
            variant="outlined"
            fullWidth
            margin="normal"
            onChange={(e) => setRoleSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Scrollable Table */}
          <TableContainer sx={{ maxHeight: 250, overflow: "auto" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Select</TableCell>
                  <TableCell>Role Name</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRoles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedRoleMap[role.id] || false}
                        onChange={() =>
                          setSelectedRoleMap((prev) => ({
                            ...prev,
                            [role.id]: !prev[role.id],
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>{role.name}</TableCell>
                    <TableCell>{role.description || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Save button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.primary[400],
                fontWeight: "bold",
              }}
              onClick={handleSaveRoles}
            >
              Save Role Assignment
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default UserList;
